import { useEffect, useState } from "react";
import type { Deal, RehabItem } from "../types";
import { listDeals, saveDeal } from "../lib/deals";
import { listMembers, isAssigned, clearTeammate, type TeamMember } from "../lib/team";
import { REHAB_CATEGORIES, rehabSubtotal, rehabTotal, blankItem } from "../lib/rehab";
import { money0 } from "../lib/format";
import { Brand } from "./Brand";

/**
 * The role-limited seat a teammate lands in. They see only the deals assigned to
 * them and submit their scope & pricing in the owner's format. Everything they
 * enter is tagged with their memberId so it merges cleanly into the owner's
 * rehab table — and it carries the same contingency buffer downstream.
 */
export function TeammateWorkspace({ teammateId, onExit }: { teammateId: string; onExit: () => void }) {
  const [me, setMe] = useState<TeamMember | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listMembers(), listDeals()]).then(([mem, d]) => {
      setMe(mem.find((m) => m.id === teammateId) ?? null);
      setDeals(d);
    }).finally(() => setLoading(false));
  }, [teammateId]);

  const assigned = deals.filter((d) => isAssigned(d, teammateId));
  const current = assigned.find((d) => d.id === openId) ?? null;

  function saveDealPatch(next: Deal) {
    setDeals((ds) => ds.map((d) => (d.id === next.id ? next : d)));
    void saveDeal(next);
  }

  return (
    <div className="app tw">
      <header className="tw-top">
        <Brand className="lp-brand" tone="light" compact />
        <div className="sp" style={{ flex: 1 }} />
        {me && <span className="badge-mode" style={{ margin: 0 }}>{me.role.toUpperCase()} · {me.name || me.email}</span>}
        <button className="btn btn-g btn-sm" onClick={() => { clearTeammate(); onExit(); }}>Exit</button>
      </header>

      <main className="tw-main">
        {loading ? (
          <p style={{ color: "var(--muted)" }}>Loading your deals…</p>
        ) : !me ? (
          <div className="card pad"><p>Your seat wasn't found. Ask the owner to re-send your invite.</p><button className="btn btn-g btn-sm" onClick={() => { clearTeammate(); onExit(); }}>Exit</button></div>
        ) : current ? (
          <DealSubmit deal={current} me={me} onBack={() => setOpenId(null)} onSave={saveDealPatch} />
        ) : (
          <>
            <h2 className="fr" style={{ color: "var(--forest)", fontSize: 24, margin: "0 0 4px" }}>Welcome, {me.name || me.email.split("@")[0]}.</h2>
            <p style={{ color: "var(--muted)", marginBottom: 16 }}>
              You're on the team as <b>{me.role}</b>. Here are the properties you've been assigned. Open one to submit your scope &amp; pricing.
            </p>
            {assigned.length === 0 ? (
              <div className="card pad"><p style={{ margin: 0, color: "var(--muted)" }}>Nothing assigned yet. The owner will assign you to a property from its <b>Rehab &amp; Team</b> section — check back soon.</p></div>
            ) : (
              <div className="grid g2">
                {assigned.map((d) => {
                  const mine = (d.rehabItems ?? []).filter((i) => i.memberId === me.id);
                  return (
                    <button key={d.id} className="card pad tw-card" onClick={() => setOpenId(d.id)}>
                      <div style={{ fontWeight: 700, color: "var(--forest)", fontSize: 16 }}>{d.name}</div>
                      <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>{d.addr || d.market}</div>
                      <div style={{ marginTop: 10 }}>
                        {mine.length
                          ? <span className="tag t-pass">✓ {mine.length} line{mine.length > 1 ? "s" : ""} · {money0(rehabSubtotal(mine))}</span>
                          : <span className="tag t-watch">Awaiting your estimate</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// --- the focused submission form for one assigned deal ----------------------
function DealSubmit({ deal, me, onBack, onSave }: { deal: Deal; me: TeamMember; onBack: () => void; onSave: (d: Deal) => void }) {
  const mine = (deal.rehabItems ?? []).filter((i) => i.memberId === me.id);
  const [items, setItems] = useState<RehabItem[]>(mine.length ? mine : [{ ...blankItem(), role: me.role, source: me.name || me.email, memberId: me.id }]);
  const [saved, setSaved] = useState(false);

  const setItem = (id: string, field: keyof RehabItem, value: string | number) =>
    setItems((its) => its.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  const addItem = () => setItems((its) => [...its, { ...blankItem(), role: me.role, source: me.name || me.email, memberId: me.id }]);
  const removeItem = (id: string) => setItems((its) => its.filter((it) => it.id !== id));

  function attach(id: string, files: FileList | null) {
    if (!files || !files.length) return;
    const names = Array.from(files).map((f) => f.name);
    setItems((its) => its.map((it) => (it.id === id ? { ...it, attachments: [...(it.attachments ?? []), ...names] } : it)));
  }

  function submit() {
    // keep everyone else's lines, replace mine, tag them to me, recompute buffered rehab
    const others = (deal.rehabItems ?? []).filter((i) => i.memberId !== me.id);
    const clean = items.filter((i) => (i.cost || 0) > 0 || i.label.trim()).map((i) => ({ ...i, role: me.role, source: me.name || me.email, memberId: me.id }));
    const nextItems = [...others, ...clean];
    onSave({ ...deal, rehabItems: nextItems, rehab: nextItems.length ? rehabTotal(nextItems, deal.contingencyPct ?? 0.1) : deal.rehab });
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  }

  const subtotal = rehabSubtotal(items);

  return (
    <>
      <button className="btn btn-g btn-sm" onClick={onBack}>← Your deals</button>
      <h2 className="fr" style={{ color: "var(--forest)", fontSize: 22, margin: "12px 0 2px" }}>{deal.name}</h2>
      <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 14 }}>{deal.addr || deal.market}</div>

      <div className="pe">
        <div className="l">◆ Your part</div>
        <p>Enter your scope and pricing below — one line per item. Attach photos or quotes if you have them.
          When you submit, it goes straight to the owner in their format. Keep it honest and complete; your numbers drive the deal.</p>
      </div>

      <div className="card pad">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="eyebrow">Your scope &amp; pricing ({me.role})</div>
          <button className="btn btn-a btn-sm" onClick={addItem}>+ Add line</button>
        </div>
        <div className="tablewrap" style={{ marginTop: 10 }}>
          <table className="t">
            <thead><tr><th>Scope</th><th>Description</th><th className="n">Your price</th><th>Files</th><th></th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>
                    <select className="rp-in" value={it.category} onChange={(e) => setItem(it.id, "category", e.target.value)}>
                      {REHAB_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td><input className="rp-in" style={{ minWidth: 160 }} value={it.label} placeholder="what's included" onChange={(e) => setItem(it.id, "label", e.target.value)} /></td>
                  <td className="n"><input className="rp-in n" type="number" min={0} style={{ width: 100, textAlign: "right" }} value={it.cost || ""} placeholder="0" onChange={(e) => setItem(it.id, "cost", Number(e.target.value) || 0)} /></td>
                  <td>
                    <label className="tw-file">📎<input type="file" multiple style={{ display: "none" }} onChange={(e) => attach(it.id, e.target.files)} /></label>
                    {(it.attachments ?? []).length > 0 && <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: 4 }}>{(it.attachments ?? []).length}</span>}
                  </td>
                  <td><button className="rp-x" title="Remove" onClick={() => removeItem(it.id)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid g2" style={{ marginTop: 12, alignItems: "center" }}>
          <div className="note">Attached files are recorded by name; the owner sees your submission instantly. (Binary upload storage arrives with the cloud bucket.)</div>
          <div className="card pad" style={{ background: "var(--cream)", margin: 0, textAlign: "right" }}>
            <div className="eyebrow" style={{ marginBottom: 2 }}>Your total</div>
            <div className="fr" style={{ fontSize: 24, fontWeight: 600, color: "var(--forest)" }}>{money0(subtotal)}</div>
          </div>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn btn-p" onClick={submit}>✓ Submit to owner</button>
          {saved && <span style={{ color: "var(--pass)", fontSize: 13 }}>✓ Submitted — the owner has your numbers.</span>}
        </div>
      </div>
    </>
  );
}
